// admin-ui/src/features/c/components/PhotosPanel.tsx
import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';

type Props = {
  locationId: number;
  canEdit?: boolean;
};

type DbPhoto = {
  id: number;
  location_id: number;
  public_url: string | null;
  width: number | null;
  height: number | null;
  caption: string | null;
  author: string | null;
  preferred_width?: number | null;
  storage_path?: string | null; // <— wichtig fürs Fallback
};

type UiPhoto = DbPhoto & {
  _caption: string;
  _author: string;
  _pw: number;
};

export default function PhotosPanel({ locationId, canEdit = false }: Props) {
  const [rows, setRows] = useState<UiPhoto[]>([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [supportsPreferredWidth, setSupportsPreferredWidth] = useState<boolean | null>(null);

  // Hilfsfunktion: Public URL auflösen (public_url oder Storage-Fallback)
  function resolvePublicUrl(p: DbPhoto): string | null {
    if (p.public_url) return p.public_url;

    // Fallback über Storage, falls nur storage_path da ist
    const path = (p.storage_path || '').trim();
    if (!path) return null;

    // Annahme: Bucket heißt "user-photos". Wenn dein Bucket anders heißt, hier anpassen.
    const { data } = supabase.storage.from('user-photos').getPublicUrl(path);
    return data?.publicUrl || null;
  }

  useEffect(() => {
    let mounted = true;

    async function load() {
      // Felder: jetzt inkl. storage_path!
      const selWithPW    = 'id,location_id,public_url,storage_path,width,height,caption,author,preferred_width';
      const selWithoutPW = 'id,location_id,public_url,storage_path,width,height,caption,author';

      let data: DbPhoto[] | null = null;
      let supports = true;

      // 1. Versuch: mit preferred_width
      const r1 = await supabase
        .from('user_photos')
        .select(selWithPW)
        .eq('location_id', locationId)
        .order('created_at', { ascending: true });

      if (r1.error) {
        // Fallback: ohne preferred_width (alte DB)
        supports = false;
        const r2 = await supabase
          .from('user_photos')
          .select(selWithoutPW)
          .eq('location_id', locationId)
          .order('created_at', { ascending: true });

        if (r2.error) {
          console.warn('[PhotosPanel] load error:', r2.error.message);
          if (mounted) { setRows([]); setSupportsPreferredWidth(supports); }
          return;
        }
        data = r2.data as DbPhoto[];
      } else {
        data = r1.data as DbPhoto[];
      }

      if (!mounted) return;

      const ui: UiPhoto[] = (data || []).map((r) => ({
        ...r,
        _caption: r.caption || '',
        _author: r.author || '',
        _pw: typeof r.preferred_width === 'number' ? (r.preferred_width as number) : 1200,
      }));

      setSupportsPreferredWidth(supports);
      setRows(ui);
    }

    load();
    return () => { mounted = false; };
  }, [locationId]);

  const hasAny = rows.length > 0;

  function updateRowLocal(id: number, patch: Partial<UiPhoto>) {
    setRows((list) => list.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }

  async function saveMetaOnServer(p: {
    location_id: number;
    public_url?: string | null;
    storage_path?: string | null;
    caption?: string | null;
    author?: string | null;
    preferred_width?: number | null;
  }) {
    const resp = await fetch('/api/user-photos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(p),
    });
    const j = await resp.json().catch(() => ({}));
    if (!resp.ok || !j?.ok) throw new Error(j?.error || `HTTP ${resp.status}`);
    return j as {
      ok: true;
      id: number;
      updated: { id: number; caption: string | null; author: string | null; preferred_width?: number | null };
    };
  }

  async function handleUpdate(row: UiPhoto) {
    setSaving((s) => ({ ...s, [row.id]: true }));
    try {
      const payload: {
        location_id: number;
        public_url?: string | null;
        storage_path?: string | null;
        caption?: string | null;
        author?: string | null;
        preferred_width?: number | null;
      } = {
        location_id: row.location_id,
        public_url: row.public_url || null,
        storage_path: row.storage_path || null,
        caption: row._caption || null,
        author: row._author || null,
      };
      if (supportsPreferredWidth) payload.preferred_width = row._pw || null;

      const result = await saveMetaOnServer(payload);
      const upd = result.updated || {};

      updateRowLocal(row.id, {
        caption: typeof upd.caption === 'undefined' ? row.caption : upd.caption,
        author: typeof upd.author === 'undefined' ? row.author : upd.author,
        preferred_width: typeof upd.preferred_width === 'number' ? upd.preferred_width : row.preferred_width,
        _caption: (upd.caption ?? row.caption ?? '') as string,
        _author: (upd.author ?? row.author ?? '') as string,
        _pw: typeof upd.preferred_width === 'number' ? (upd.preferred_width as number) : row._pw,
      });

      alert('Gespeichert');
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || String(e)));
    } finally {
      setSaving((s) => ({ ...s, [row.id]: false }));
    }
  }

  async function handleDelete(row: UiPhoto) {
    if (!confirm('Foto wirklich löschen?')) return;
    setBusy(true);
    try {
      const { error } = await supabase.from('user_photos').delete().eq('id', row.id);
      if (error) throw error;
      setRows((list) => list.filter((it) => it.id !== row.id));
    } catch (e: any) {
      alert('Löschen fehlgeschlagen: ' + (e?.message || String(e)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {!hasAny && <div style={{ opacity: 0.7 }}>Noch keine User-Fotos vorhanden.</div>}

      {rows.map((row) => {
        const displayW = Math.max(320, Math.min(1600, Number(row._pw || 1200)));
        const base = resolvePublicUrl(row);
        const src = base ? `${base}${base.includes('?') ? '&' : '?'}width=${displayW}` : '';

        return (
          <div
            key={row.id}
            style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, background: '#fafafa' }}
          >
            {src ? (
              <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #eee', marginBottom: 8 }}>
                <img
                  src={src}
                  alt={row._caption || ''}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : (
              <div style={{ padding: 12, border: '1px dashed #d1d5db', borderRadius: 10, marginBottom: 8 }}>
                Kein Bild-URL gefunden
              </div>
            )}

            {canEdit ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, alignItems: 'center' }}>
                  <input
                    placeholder="Bildtitel / Caption"
                    value={row._caption}
                    onChange={(e) => updateRowLocal(row.id, { _caption: e.target.value })}
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8 }}
                  />
                  <input
                    placeholder="Autor / Credit"
                    value={row._author}
                    onChange={(e) => updateRowLocal(row.id, { _author: e.target.value })}
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8 }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', marginTop: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
                      Anzeigegröße (Breite in px)
                      {supportsPreferredWidth === false && (
                        <span style={{ marginLeft: 8, opacity: 0.6 }}>
                          (nur UI – Spalte <code>preferred_width</code> nicht vorhanden)
                        </span>
                      )}
                    </div>
                    <input
                      type="range"
                      min={320}
                      max={1600}
                      step={40}
                      value={displayW}
                      onChange={(e) => updateRowLocal(row.id, { _pw: Number(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.8, textAlign: 'right' }}>
                    {row.width && row.height ? `${row.width}×${row.height}px` : '—'} · Vorschau: {displayW}px
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleUpdate(row); }}
                    disabled={!!saving[row.id]}
                    style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#0ea5e9', color: '#fff', fontWeight: 600 }}
                  >
                    {saving[row.id] ? 'Speichern…' : 'Aktualisieren'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(row); }}
                    disabled={busy}
                    style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#111827', color: '#fff' }}
                  >
                    Löschen
                  </button>
                </div>
              </>
            ) : (
              <div style={{ display: 'grid', gap: 4, fontSize: 13, color: '#475569' }}>
                {row.caption ? <div><strong>Titel:</strong> {row.caption}</div> : null}
                {row.author ? <div><strong>Credit:</strong> {row.author}</div> : null}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
